import cx from 'classnames'
import { find, uniq, uniqBy } from 'lodash/fp'
import { useState } from 'react'
import BareWarningIcon from '~/components/icons/BareWarningIcon'
import Select, { SelectOption } from '~/components/Select'
import { displayCount } from '~/utils/string'
import { ClePhase, ClePlate } from '../clePlates/ClePlate.interface'
import { CLE_PLATES } from '../clePlates/generateClePipelinePlates'
import ClePipelineViz from './ClePIpelineViz'
import ClePipelineGrid from './ClePipelineGrid'
import cs from './cle_pipeline_view.scss'

const getNumCellLines = (plates: ClePlate[]) => {
  return uniq(plates.map(plate => plate.cellLine.cellLineIndex)).length
}

const getNumCellLinesRequiringReview = (plates: ClePlate[]) => {
  const platesWithAlerts = plates.filter(plate => plate.hasAlert)
  return uniq(platesWithAlerts.map(plate => plate.cellLine.cellLineIndex)).length
}

const getOwnerSelectOptions = (plates: ClePlate[]) => {
  const owners = uniq(plates.map(plate => plate.owner))
  return owners.map(owner => ({
    key: owner,
    label: owner,
  }))
}

const getCellLineSelectOptions = (plates: ClePlate[]) => {
  const cellLines = uniqBy(
    'cellLineIndex',
    plates.map(plate => plate.cellLine),
  )
  return cellLines.map(cellLine => ({
    key: String(cellLine.cellLineIndex),
    label: cellLine.name,
  }))
}

const itemMatchesQuery = (item, queryLowerCase) =>
  item && item.label.toLowerCase().includes(queryLowerCase)

const ClePipelineView = ({ useLocalAssets }: { useLocalAssets: boolean }) => {
  // Just calculate the plates once per re-fresh.
  const clePipelinePlates = CLE_PLATES

  const [selectedOwner, setSelectedOwner] = useState<string | null>(null)
  const [selectedCellLineIndex, setSelectedCellLineIndex] = useState<number | null>(
    null,
  )
  const [selectedPhase, setSelectedPhase] = useState<ClePhase | null>(null)

  const handleSelectedOwnerChange = (value: SelectOption) => {
    setSelectedOwner(value.label)
  }

  const handleSelectedCellLineChange = (value: SelectOption) => {
    setSelectedCellLineIndex(value.key === null ? null : Number(value.key))
  }

  const getFilteredPlates = () => {
    return clePipelinePlates
      .filter(plate => selectedOwner === null || plate.owner === selectedOwner)
      .filter(
        plate =>
          selectedCellLineIndex === null ||
          plate.cellLine.cellLineIndex === selectedCellLineIndex,
      )
      .filter(plate => selectedPhase === null || plate.phase === selectedPhase)
  }

  const renderControls = () => {
    const ownerSelectOptions = getOwnerSelectOptions(clePipelinePlates)
    const cellLineSelectOptions = getCellLineSelectOptions(clePipelinePlates)
    return (
      <div className={cs.controls}>
        <Select<SelectOption>
          label='Owner'
          items={ownerSelectOptions}
          itemKey='key'
          itemLabelKey='label'
          filterable
          itemMatchesQuery={itemMatchesQuery}
          activeItem={find(['label', selectedOwner], ownerSelectOptions) || null}
          onChange={handleSelectedOwnerChange}
          className={cs.select}
          triggerClassName={cs.trigger}
          popoverClassName={cs.popover}
          allowNoneOption
          noneOptionText='All'
        />
        <Select<SelectOption>
          label='Cell Line'
          items={cellLineSelectOptions}
          itemKey='key'
          itemLabelKey='label'
          filterable
          itemMatchesQuery={itemMatchesQuery}
          activeItem={
            find(
              option => option.key === String(selectedCellLineIndex),
              cellLineSelectOptions,
            ) || null
          }
          onChange={handleSelectedCellLineChange}
          className={cs.select}
          triggerClassName={cs.trigger}
          popoverClassName={cs.popover}
          allowNoneOption
          noneOptionText='All'
        />
      </div>
    )
  }

  const filteredPlates = getFilteredPlates()
  const numCellLinesRequiringReview = getNumCellLinesRequiringReview(clePipelinePlates)

  return (
    <div className={cx(cs.clePipelineView)}>
      <div className={cs.header}>
        <div className={cs.left}>
          <div className={cs.title}>Cell Line Engineering</div>
          <div className={cs.subtitle}>
            {displayCount('cell line', getNumCellLines(clePipelinePlates))} in progress
          </div>
        </div>
        <div className={cs.fill} />
        <div className={cs.alertMessage}>
          <BareWarningIcon className={cs.alertIcon} />
          {displayCount('cell line', numCellLinesRequiringReview)}{' '}
          {numCellLinesRequiringReview === 1 ? 'requires' : 'require'} review
        </div>
      </div>
      <ClePipelineViz
        plates={clePipelinePlates}
        selectedPhase={selectedPhase}
        setSelectedPhase={setSelectedPhase}
      />
      {renderControls()}
      <ClePipelineGrid plates={filteredPlates} useLocalAssets={useLocalAssets} />
    </div>
  )
}

export default ClePipelineView
